import React, {useRef, useState} from 'react';
import {
    ActionButton,
    Dropdown,
    IconButton,
    Image,
    ImageFit,
    Label,
    Spinner,
    SpinnerSize,
    Stack,
    Text
} from '@fluentui/react';
import {toast} from 'react-toastify';
import {IStorefrontTemplateGalleryItem} from '../../../models/Storefront';
import {
    useDeleteStorefrontTemplateGalleryMediaMutation,
    useGetStorefrontTemplateQuery,
    useUpdateStorefrontTemplateMutation,
    useUploadStorefrontTemplateGalleryMediaMutation
} from '../../../store/endpoints/StorefrontTemplateEndpoints';

const ACCEPTED_IMAGE_TYPES = 'image/png,image/jpeg,image/gif,image/webp';
const MAX_FILE_BYTES = 20 * 1024 * 1024; // 20 MB

interface ITemplateGalleryEditorProps {
    templateId: string;
    // When true, per-thumbnail left/right buttons reorder the gallery (persisted via PUT).
    showReorder?: boolean;
}

// Shared gallery management used by both the save-as-template and edit-template modals:
// multi-file image upload (base64 via the endpoint), per-item remove, and optional reorder.
const TemplateGalleryEditor: React.FC<ITemplateGalleryEditorProps> = (props) => {
    const {templateId, showReorder} = props;

    const {data: template, isLoading, isFetching} = useGetStorefrontTemplateQuery(templateId, {skip: !templateId});
    const [uploadMedia] = useUploadStorefrontTemplateGalleryMediaMutation();
    const [deleteMedia] = useDeleteStorefrontTemplateGalleryMediaMutation();
    const [updateTemplate] = useUpdateStorefrontTemplateMutation();

    const [uploading, setUploading] = useState(false);
    const [busyItemId, setBusyItemId] = useState<string | undefined>();
    const inputRef = useRef<HTMLInputElement | null>(null);

    const gallery: IStorefrontTemplateGalleryItem[] = [...(template?.gallery ?? [])]
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

    const onPickFiles = () => {
        if (uploading) return;
        inputRef.current?.click();
    };

    const onFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []);
        if (files.length === 0) return;

        setUploading(true);
        let anyFail = false;
        let anySuccess = false;
        for (const file of files) {
            if (file.size > MAX_FILE_BYTES) {
                toast.error(`${file.name} exceeds the 20 MB limit`);
                anyFail = true;
                continue;
            }
            try {
                const buffer = await file.arrayBuffer();
                const res = await uploadMedia({id: templateId, file: buffer, fileName: file.name}).unwrap();
                if (res.success) {
                    anySuccess = true;
                } else {
                    anyFail = true;
                    toast.error(res.message || `Failed to upload ${file.name}`);
                }
            } catch {
                anyFail = true;
                toast.error(`Failed to upload ${file.name}`);
            }
        }
        setUploading(false);
        if (anySuccess && !anyFail) {
            toast.success(files.length > 1 ? 'Preview images uploaded' : 'Preview image uploaded');
        }
        if (inputRef.current) inputRef.current.value = '';
    };

    const onRemove = async (item: IStorefrontTemplateGalleryItem) => {
        if (!item.id) return;
        setBusyItemId(item.id);
        try {
            const res = await deleteMedia({id: templateId, itemId: item.id}).unwrap();
            if (res.success) {
                toast.success('Preview media removed');
            } else {
                toast.error(res.message || 'Failed to remove media');
            }
        } catch {
            toast.error('Failed to remove media');
        }
        setBusyItemId(undefined);
    };

    // Tag an image with the palette it showcases (or clear the tag). The picker's details view only
    // shows palette-tagged images while their palette is selected; untagged images always show.
    const onSetPalette = async (item: IStorefrontTemplateGalleryItem, paletteId?: string) => {
        if (!template || !item.id) return;
        const newGallery = (template.gallery ?? []).map((it) =>
            it.id === item.id ? {...it, paletteId: paletteId || undefined} : it);
        setBusyItemId(item.id);
        try {
            const res = await updateTemplate({id: templateId, template: {...template, gallery: newGallery}}).unwrap();
            if (!res.success) toast.error(res.message || 'Failed to update the image palette');
        } catch {
            toast.error('Failed to update the image palette');
        }
        setBusyItemId(undefined);
    };

    const onReorder = async (idx: number, direction: 'left' | 'right') => {
        if (!template) return;
        const swapIdx = direction === 'left' ? idx - 1 : idx + 1;
        if (swapIdx < 0 || swapIdx >= gallery.length) return;

        const a = gallery[idx];
        const b = gallery[swapIdx];
        const aSort = a.sortOrder ?? idx;
        const bSort = b.sortOrder ?? swapIdx;
        const newGallery = gallery.map((it) => {
            if (it.id === a.id) return {...it, sortOrder: bSort};
            if (it.id === b.id) return {...it, sortOrder: aSort};
            return it;
        });

        setBusyItemId(a.id);
        try {
            // The PUT carries the whole template view model; the API only honors gallery here.
            const res = await updateTemplate({id: templateId, template: {...template, gallery: newGallery}}).unwrap();
            if (res.success) {
                toast.success('Preview order updated');
            } else {
                toast.error(res.message || 'Failed to reorder preview media');
            }
        } catch {
            toast.error('Failed to reorder preview media');
        }
        setBusyItemId(undefined);
    };

    return (
        <Stack tokens={{childrenGap: 8}}>
            <Label>Preview media</Label>
            <Text variant="small">
                Add preview images shown in the template picker. PNG, JPEG, GIF, or WEBP up to 20 MB each.
            </Text>

            <input
                type="file"
                ref={inputRef}
                multiple
                accept={ACCEPTED_IMAGE_TYPES}
                style={{display: 'none'}}
                onChange={onFilesSelected}
            />

            <Stack horizontal wrap tokens={{childrenGap: 12}}>
                {gallery.map((item, idx) => (
                    <div
                        key={item.id ?? idx}
                        style={{
                            position: 'relative',
                            width: 140,
                            border: '1px solid #ddd',
                            borderRadius: 4,
                            padding: 4
                        }}
                    >
                        {item.mediaType === 'video' ? (
                            <video
                                src={item.url}
                                style={{width: '100%', height: 90, objectFit: 'cover', borderRadius: 2}}
                                controls
                            />
                        ) : (
                            <Image
                                src={item.url}
                                alt={item.altText ?? 'Template preview'}
                                width={132}
                                height={90}
                                imageFit={ImageFit.cover}
                                styles={{root: {borderRadius: 2}}}
                            />
                        )}
                        <IconButton
                            iconProps={{iconName: 'Delete'}}
                            title="Remove"
                            ariaLabel="Remove preview media"
                            disabled={busyItemId === item.id}
                            onClick={() => onRemove(item)}
                            style={{position: 'absolute', top: 6, right: 6, background: 'rgba(255,255,255,0.85)'}}
                        />
                        {(template?.palettes?.length ?? 0) > 0 && (
                            <Dropdown
                                ariaLabel="Palette this image showcases"
                                selectedKey={item.paletteId ?? ''}
                                disabled={busyItemId === item.id}
                                options={[
                                    {key: '', text: 'All palettes'},
                                    ...(template?.palettes ?? []).map((p) => ({key: p.id ?? '', text: p.name ?? ''})),
                                ]}
                                onChange={(_, option) => onSetPalette(item, (option?.key as string) || undefined)}
                                styles={{root: {marginTop: 4}, title: {fontSize: 11, height: 24, lineHeight: 22}, caretDownWrapper: {height: 24, lineHeight: 22}}}
                            />
                        )}
                        {showReorder && (
                            <Stack horizontal horizontalAlign="center" tokens={{childrenGap: 4}}>
                                <IconButton
                                    iconProps={{iconName: 'ChevronLeft'}}
                                    title="Move earlier"
                                    ariaLabel="Move earlier"
                                    disabled={idx === 0 || busyItemId === item.id}
                                    onClick={() => onReorder(idx, 'left')}
                                />
                                <IconButton
                                    iconProps={{iconName: 'ChevronRight'}}
                                    title="Move later"
                                    ariaLabel="Move later"
                                    disabled={idx === gallery.length - 1 || busyItemId === item.id}
                                    onClick={() => onReorder(idx, 'right')}
                                />
                            </Stack>
                        )}
                    </div>
                ))}
            </Stack>

            <Stack horizontal verticalAlign="center" tokens={{childrenGap: 8}}>
                <ActionButton
                    iconProps={{iconName: 'Add'}}
                    disabled={uploading || !templateId}
                    onClick={onPickFiles}
                >
                    Add preview images
                </ActionButton>
                {(uploading || isLoading || isFetching) && (
                    <Spinner size={SpinnerSize.small} label={uploading ? 'Uploading...' : 'Loading...'} labelPosition="right"/>
                )}
            </Stack>
        </Stack>
    );
};

export default TemplateGalleryEditor;
